'use client';

import { get, sortBy } from 'lodash-es';
import { useQuery, useMutation } from '@apollo/client/react';
import { useRouter } from 'next/navigation';
import { Formik } from 'formik';
import Typography from '@mui/material/Typography';
import GroupForm, { groupSchema } from '@/components/groups/form';
import { createGroupMutation } from '@/apollo/groups';
import { primaryCityQuery } from '@/apollo/cities';
import { meQuery } from '@/apollo/auth';
import { myGroupsQuery } from '@/apollo/groups';
import LoginRequiredNotice from '@/components/login-required-notice';
import Loading from '@/components/loading';
import ErrorMessage from '@/components/error-message';
import Main from '@/components/main';
import { useAppContext } from '@/contexts/app';

const GroupNewPageClient = () => {
  const { isLoggedIn } = useAppContext();
  const router = useRouter();

  const { data, loading: primaryCityLoading, error: primaryCityError } = useQuery(primaryCityQuery, { ssr: false });
  const primaryCity = data?.me?.primaryCity;

  const [createGroup] = useMutation(createGroupMutation, {
    update: (proxy, { data: { createGroup: newGroup } }) => {
      try {
        const data = proxy.readQuery({ query: meQuery });
        proxy.writeQuery({
          query: meQuery,
          data: {
            ...data,
            me: {
              ...data.me,
              groupMembers: [...data.me.groupMembers, newGroup.membership]
            }
          }
        });
      } catch {
        // do nothing
      }

      try {
        const data = proxy.readQuery({ query: myGroupsQuery });
        proxy.writeQuery({
          query: myGroupsQuery,
          data: {
            ...data,
            me: {
              ...data.me,
              groups: sortBy([...data.me.groups, newGroup], 'name')
            }
          }
        });
      } catch {
        // do nothing
      }
    }
  });

  if (primaryCityLoading) {
    return <Loading />;
  }
  if (primaryCityError) {
    return (
      <ErrorMessage
        message={primaryCityError.message}
        error={primaryCityError}
      />
    );
  }

  return (
    <Main compact>
      <Typography variant="h1">Create Group</Typography>

      {!isLoggedIn && <LoginRequiredNotice returnTo="/groups/new" action="create a group" />}

      <Formik
        initialValues={{
          public: true,
          discoverable: true,
          createEventPermission: 'OWNER',
          createPostPermission: 'OWNER',
          uploadImagePermission: 'MEMBER',
          cityId: get(primaryCity, 'id'),
        }}
        validationSchema={groupSchema({ editing: false })}
        onSubmit={(values, actions) => {
          createGroup({
            variables: {
              data: values,
            },
          }).then(({ data: { createGroup: group } }) => {
            router.push(`/groups/${group.slug}`);
          }).catch((error) => {
            actions.setSubmitting(false);
            console.log(error);
          });
        }}
      >
        <GroupForm disabled={!isLoggedIn} />
      </Formik>
    </Main>
  );
};

export default GroupNewPageClient;
